/**************************************************************************************************************************************************
*   MODULE NAME     :  DataTable
*   AUTHOR          :  GLSD DEV Team
*   DATE            :  05-01-2021
*   DESCRIPTION     :  All Positive workflows logic(Rows , Columns, Pagination, Search, CSVExport , Row count per Page )
*   Copyright 2021 Eli Lilly and Company
*   All rights reserved
*
/**************************************************************************************************************************************************
*   *** CHANGE HISTORY ***
/**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG1689267
*   DESCRIPTION: <GLSD>: New System Development
/**************************************************************************************************************************************************
*   TRACKING NUMBER: CHG2671327
*   DESCRIPTION: <GLSD>: Fix Dependabot Vulnerabilities
*   Updated import for react-bootstrap-table2-toolkit package
*   DATE: 02-Dec-2025
/**************************************************************************************************************************************************/

import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationTotalStandalone,
  PaginationListStandalone,
  SizePerPageDropdownStandalone
} from "react-bootstrap-table2-paginator";
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import ToolkitProvider, { Search, CSVExport } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import filterFactory from "react-bootstrap-table2-filter";
import { Row, Col } from "react-bootstrap";
import Scrollbar from "react-scrollbars-custom";
import { FaFileExport } from 'react-icons/fa';

import "./DataTable.css"

const DataTable = props => {
  const { SearchBar, ClearSearchButton } = Search;
  const { ExportCSVButton } = CSVExport;
  const {
    data,
    columns,
    defaultSortField,
    keyField,
    csvName,
    message,
    showSearch = true,
    showPagination = true,
    totalSize,
    page,
    cellEdit,
    rowClasses,
    searchCount,
    sortOrder,
    sizePerPage,
    remote,
    pageProps,
    selectRow,
    ...tableProps
  } = props;
  const customTotal = (from, to, size, total) => {
    if (data.length > 0) {
      if (remote) {
        return searchCount ? (
          <span className="react-bootstrap-table-pagination-total">
            {`Showing ${from} to ${to} of ${searchCount} entries`}
            {totalSize !== searchCount &&
              ` (filtered from ${totalSize} total entries)`}
          </span>
        ) : (
            <span />
          );
      } else {
        return (
          <span className="react-bootstrap-table-pagination-total">
            {`Showing ${from} to ${to} of ${size} entries`}
            {data.length !== size &&
              ` (filtered from ${data.length} total entries)`}
          </span>
        );
      }
    } else {
      return <span />;
    }
  };
 
  const onTableChange = (type, options) => {
    const { loadData } = props;
    loadData(options);
  };
  /**
        * define default option object for data table
     **/
  const options = {
    custom: true,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    prePageText: "Prev",
    nextPageText: "Next",
    prePageTitle: "Prev Page",
    nextPageTitle: "Next Page",
    showTotal: data && data.length > 0,
    paginationTotalRenderer: customTotal,
    totalSize: searchCount || data.length,
    page,
    ...pageProps,
    sizePerPageList: sizePerPage ? sizePerPage :[25, 30, 50, 100]
  };
  /**
    * define default sorting datafield and order
 **/
  const defaultSorted = defaultSortField
    ? [
      {
        dataField: defaultSortField,
        order: sortOrder
      }
    ]
    : [];
    const remoteProps = remote  ? {   onTableChange   }  : {};
  const table = (toolKitProps, paginationTableProps) => (
    <BootstrapTable
      headerClasses="tablePositionRow"
      noDataIndication="No Records Found"
      wrapperClasses="table-responsive"
      defaultSorted={defaultSorted}
      cellEdit={cellEdit}
      rowClasses={rowClasses}
      filter={filterFactory()}
      remote={remote}
      {...toolKitProps.baseProps}
      {...paginationTableProps}
      {...remoteProps}
      striped
      hover
      condensed
      selectRow={selectRow}
    />
  );

  return (
    <ToolkitProvider
      data={Array.isArray(data) ? data : []}
      columns={columns}
      search={{ searchFormatted: true }}
      keyField={keyField}
      bootstrap4
      {...tableProps}
      exportCSV={
        {
          fileName: `${csvName}.csv`,
          blobType: 'text/csv;charset=ansi',
          onlyExportFiltered: true,
          exportAll: false
        }
      }
    >
      {toolKitProps => (
        <>
          <PaginationProvider pagination={paginationFactory(options)}>
            {({ paginationProps, paginationTableProps }) => (
              <div className="datatable-wrap">
                <Row>
                  {showPagination &&
                    <Col xs={2} className="align-items-center d-flex pr-0 mt-minus-38 ">
                      <label className="mt-1">Show</label>
                      <SizePerPageDropdownStandalone
                        {...paginationProps}
                        className="mx-1"
                      />
                      <label className="mt-1">entries</label>
                    </Col>
                  }
                  {showSearch &&
                    <Col xs={3} className="align-items-center d-flex pr-0 mt-minus-38">
                      <SearchBar {...toolKitProps.searchProps} />
                      &nbsp;
                      <ClearSearchButton {...toolKitProps.searchProps} className="btn btn-primary" />
                    </Col>
                  }
                  <Col xs={12} className="table-wrap-row">
                    <div className="table-wrap">
                        {table(toolKitProps, paginationTableProps)}
                    </div>
                  </Col>
                </Row>
                {showPagination &&
                  <Row className="mt-3">
                    <Col xs={5} className="pt-2">
                      <PaginationTotalStandalone {...paginationProps} />
                    </Col>
                    <Col className="text-right">
                      <PaginationListStandalone {...paginationProps} />
                    </Col>
                  </Row>
                }
              </div>
            )}
          </PaginationProvider>
          {
            csvName === false ? null :
              <Row>
                <Col className="mt-2">
                  <ExportCSVButton {...toolKitProps.csvProps} >Export CSV<FaFileExport /></ExportCSVButton>
                </Col>
                
              </Row>
          }
        </>
      )}
    </ToolkitProvider>
  );
};
DataTable.defaultProps = {
  data: [],
  remote: false,
  sortOrder: "desc",
  sizePerPage: [25, 30, 50, 100],
  page: 1,
  csvName : false,
  
};
export default DataTable;
